/**
 * Fase-1 migratie-helper voor de multi-instance-refactor (2026-05-26).
 *
 * Vult `type` ("object"|"relation") + `ref` (UUID van onderliggend concept
 * of relation) op viewState-entries die nog uit de pre-refactor-tijd komen.
 * Idempotent — entries die `type`+`ref` al hebben blijven onaangeroerd.
 *
 * Detectie-volgorde (voor ÉÉN entry met key K en value V):
 *  1. Heeft V al een correct `type`? → respecteer 'm.
 *     - "object": ref = V.ref ?? V.conceptRef ?? K
 *     - "relation": ref = V.ref ?? K
 *  2. V.conceptRef gezet → "object", ref = V.conceptRef.
 *  3. V.position gezet → "object", ref = K (canonieke placement).
 *  4. K resolveert naar een concept in de store → "object", ref = K.
 *  5. K resolveert naar een relation in de store → "relation", ref = K.
 *  6. Geen match → orphan, entry blijft ongewijzigd (caller mag besluiten
 *     hem te purgen via aparte GC; deze helper raakt 'm niet aan).
 *
 * Gebruik in twee paden:
 *
 *  - **Bulk-migratie** bij hydrate (workspace-store-init, snapshot-restore,
 *    JSON-import, AMEFF-import). Voert één pass over alle diagram-
 *    viewStates uit en schrijft de aanvullingen terug.
 *
 *  - **Read-time lazy backfill** als vangnet voor entries die door een
 *    pad zijn binnengekomen dat de bulk-migratie nog niet raakte. De
 *    Zustand-store roept deze helper aan op de eerstvolgende selector-
 *    pass van een diagram, zodat de aanvulling persistent wordt zonder
 *    expliciete user-actie.
 */
import type { ViewElementState, ViewState } from "./types";

/**
 * Minimaal resolver-interface waar de migratie tegen werkt. Compatible
 * met `WorkspaceStore` (heeft `getConcept`/`getRelation`) maar ook met
 * lichtere callers die alleen Sets-of-ids hebben (zie `setResolver`).
 */
export interface EntityResolver {
  hasConcept(id: string): boolean;
  hasRelation(id: string): boolean;
}

/**
 * Adapter voor callers die alleen `WorkspaceStore` of een ander object
 * met `getConcept`/`getRelation`-methods hebben.
 */
export function storeResolver(store: {
  getConcept(id: string): unknown;
  getRelation(id: string): unknown;
}): EntityResolver {
  return {
    hasConcept: (id) => store.getConcept(id) !== undefined,
    hasRelation: (id) => store.getRelation(id) !== undefined,
  };
}

/**
 * Adapter voor callers (import-handlers) die alleen Sets-of-ids hebben
 * — bv. tijdens parse-vóór-insert van een import-payload.
 */
export function setResolver(
  conceptIds: ReadonlySet<string>,
  relationIds: ReadonlySet<string>,
): EntityResolver {
  return {
    hasConcept: (id) => conceptIds.has(id),
    hasRelation: (id) => relationIds.has(id),
  };
}

/**
 * Resultaat van een single-entry-migratie. `changed` = true wanneer
 * de helper een aanvulling heeft gedaan; `entry` is de (nieuwe) waarde
 * die teruggeschreven moet worden. Bij orphan returnt de helper
 * `{ changed: false, entry, orphan: true }`.
 */
export interface MigrateEntryResult {
  changed: boolean;
  entry: ViewElementState;
  orphan: boolean;
}

/**
 * Migreer één viewState-entry. Returnt een nieuw object alleen wanneer
 * er iets veranderde; anders het originele object (referentiële
 * gelijkheid behouden zodat React-memoization niet onnodig invalidates).
 */
export function migrateViewStateEntry(
  key: string,
  entry: ViewElementState,
  resolver: EntityResolver,
): MigrateEntryResult {
  // Stap 0: synthetische super-node (Fase 2 diagram-vereenvoudiging) —
  // heeft géén onderliggend concept/relation (ref = eigen entry-key), dus
  // NOOIT als orphan markeren of hertypen. Zonder deze guard zou de
  // position-heuristiek in stap 3 'm naar type:"object" + orphan:true
  // herschrijven en zou een GC-pass 'm kunnen opruimen.
  if (entry.type === "supernode") {
    if (entry.ref === key) {
      return { changed: false, entry, orphan: false };
    }
    return { changed: true, entry: { ...entry, ref: key }, orphan: false };
  }

  // Stap 1: type al correct gezet?
  if (entry.type === "object") {
    // Legacy fallback: pre-Fase-2 entries kunnen nog `conceptRef`-veld
    // hebben (uit D1-blobs die nooit door admin-migration gingen).
    const legacyConceptRef = (entry as { conceptRef?: string }).conceptRef;
    const ref = entry.ref ?? legacyConceptRef ?? key;
    if (entry.ref === ref) {
      return { changed: false, entry, orphan: false };
    }
    return { changed: true, entry: { ...entry, ref }, orphan: false };
  }
  if (entry.type === "relation") {
    const ref = entry.ref ?? key;
    if (entry.ref === ref) {
      return { changed: false, entry, orphan: false };
    }
    return { changed: true, entry: { ...entry, ref }, orphan: false };
  }

  // Stap 2: oude (Fase-0) `conceptRef`-veld gezet → object-instance.
  // `conceptRef` is in Fase-2-cleanup gedropt uit ViewElementState; voor
  // legacy-data die nog in D1 staat (en nog niet door admin-migration
  // gegaan is) lezen we 't via een cast.
  const legacyConceptRef = (entry as { conceptRef?: string }).conceptRef;
  if (legacyConceptRef) {
    return {
      changed: true,
      entry: { ...entry, type: "object", ref: legacyConceptRef },
      orphan: false,
    };
  }

  // Stap 3: position gezet maar geen conceptRef → canonieke object-placement.
  // (Concept-instances hebben altijd een position; relation-entries niet.)
  if (entry.position !== undefined) {
    // Verifiëer dat de key inderdaad naar een concept resolveert; zo niet
    // is 't een orphan (concept hard-weg). We respecteren de position-hint
    // wel zodat een tijdelijk-niet-resolvable concept (bv. async-hydrate
    // race) niet als relation gemerkt wordt.
    if (resolver.hasConcept(key)) {
      return {
        changed: true,
        entry: { ...entry, type: "object", ref: key },
        orphan: false,
      };
    }
    // Geen concept maar wel position → orphan-object. Markeer minimaal als
    // object zodat downstream-code een consistente shape ziet, maar geef
    // orphan-flag terug zodat een GC-pass 'm kan opruimen.
    return {
      changed: true,
      entry: { ...entry, type: "object", ref: key },
      orphan: true,
    };
  }

  // Stap 4-5: geen position, geen conceptRef → resolveer via store.
  if (resolver.hasConcept(key)) {
    return {
      changed: true,
      entry: { ...entry, type: "object", ref: key },
      orphan: false,
    };
  }
  if (resolver.hasRelation(key)) {
    return {
      changed: true,
      entry: { ...entry, type: "relation", ref: key },
      orphan: false,
    };
  }

  // Stap 6: orphan — geen aanvulling, GC's keuze.
  return { changed: false, entry, orphan: true };
}

/**
 * Resultaat van bulk-migratie over één diagram's viewState.
 * - `viewState`: het (mogelijk nieuwe) viewState-object. Identiek aan
 *   input wanneer er niets veranderde (referentiële gelijkheid).
 * - `migrated`: aantal entries dat een type/ref-aanvulling kreeg.
 * - `orphanKeys`: entry-keys die niet naar een bekende concept/relation
 *   resolved. Caller besluit of 'ie hier GC'd.
 */
export interface MigrateDiagramResult {
  viewState: ViewState;
  migrated: number;
  orphanKeys: string[];
}

/**
 * Migreer alle entries in één diagram's viewState. Idempotent: een
 * eerder gemigreerd viewState wordt onveranderd teruggegeven (referentieel
 * gelijk aan input → React-memoization blijft hits geven).
 */
export function normalizeViewState(
  viewState: ViewState,
  resolver: EntityResolver,
): MigrateDiagramResult {
  let migrated = 0;
  const orphanKeys: string[] = [];
  let mutated: ViewState | null = null;

  for (const key of Object.keys(viewState)) {
    const original = viewState[key];
    if (original === undefined) continue;
    const result = migrateViewStateEntry(key, original, resolver);
    if (result.orphan) orphanKeys.push(key);
    if (result.changed) {
      if (!mutated) mutated = { ...viewState };
      mutated[key] = result.entry;
      migrated++;
    }
  }

  return {
    viewState: mutated ?? viewState,
    migrated,
    orphanKeys,
  };
}
